/**
 * 可拖拽组织树组件
 * 支持拖拽排序、右键菜单、搜索过滤
 */

import React, { useState, useMemo, useCallback } from 'react';
import { Tree, Input, Dropdown, Tag, Space, Empty, Spin } from 'antd';
import { message, modal } from '@/utils/antdStatic';
import type { TreeDataNode } from 'antd';
import {
  ApartmentOutlined,
  BankOutlined,
  TeamOutlined,
  UserOutlined,
  CrownOutlined,
  EditOutlined,
  PlusOutlined,
  DeleteOutlined,
  EyeOutlined,
  SearchOutlined,
  DownOutlined,
} from '@ant-design/icons';
import type { Organization } from '@/modules/foundation/api/types';
import { OrganizationType } from '@/modules/foundation/api/types';
import type { OrganizationTreeProps, OrganizationTreeNode, DragInfo, ContextMenuItem } from './types';
import './index.less';

const { Search } = Input;

/** 组织类型配置 */
const ORG_TYPE_CONFIG: Record<OrganizationType, { color: string; text: string; icon: React.ReactNode }> = {
  [OrganizationType.COMPANY]: { color: 'purple', text: '公司', icon: <BankOutlined /> },
  [OrganizationType.DEPARTMENT]: { color: 'blue', text: '部门', icon: <ApartmentOutlined /> },
  [OrganizationType.GROUP]: { color: 'green', text: '小组', icon: <TeamOutlined /> },
  [OrganizationType.TEAM]: { color: 'orange', text: '团队', icon: <TeamOutlined /> },
};

/** 层级配置 */
const LEVEL_CONFIG: Record<number, { color: string; text: string }> = {
  1: { color: 'red', text: 'L1' },
  2: { color: 'orange', text: 'L2' },
  3: { color: 'green', text: 'L3' },
  4: { color: 'blue', text: 'L4' },
};

const OrganizationTree: React.FC<OrganizationTreeProps> = ({
  organizations,
  loading = false,
  selectedKey,
  expandedKeys: propsExpandedKeys,
  draggable = true,
  showSearch = true,
  showActions = true,
  onSelect,
  onExpand,
  onDrop,
  onEdit,
  onAddChild,
  onDelete,
  onViewDetail,
  onViewMembers,
  className,
  style,
  height,
}) => {
  const [searchValue, setSearchValue] = useState('');
  const [internalExpandedKeys, setInternalExpandedKeys] = useState<string[]>([]);
  const [autoExpandParent, setAutoExpandParent] = useState(true);
  const [contextMenuOrg, setContextMenuOrg] = useState<Organization | null>(null);

  // 展开的keys，优先使用外部传入的
  const expandedKeys = propsExpandedKeys ?? internalExpandedKeys;

  // 构建树形数据
  const buildTreeData = useCallback((orgs: Organization[], searchText: string): OrganizationTreeNode[] => {
    const orgMap = new Map<string | number, Organization>();
    const childrenMap = new Map<string | number, Organization[]>();

    // 建立映射（API响应经过keysToCamel转换，parent_id变为parentId）
    orgs.forEach(org => {
      orgMap.set(org.id, org);
      const parentId = (org as any).parentId ?? org.parent_id ?? 0;
      if (!childrenMap.has(parentId)) {
        childrenMap.set(parentId, []);
      }
      childrenMap.get(parentId)!.push(org);
    });

    // 递归构建节点
    const buildNode = (org: Organization): OrganizationTreeNode | null => {
      const children = childrenMap.get(org.id) || [];
      const typeConfig = ORG_TYPE_CONFIG[org.type] || ORG_TYPE_CONFIG[OrganizationType.DEPARTMENT];
      const levelConfig = LEVEL_CONFIG[org.level] || { color: 'default', text: `L${org.level}` };

      // 搜索过滤
      const matchSearch = !searchText ||
        org.name.toLowerCase().includes(searchText.toLowerCase()) ||
        org.code.toLowerCase().includes(searchText.toLowerCase());

      const childNodes = children
        .sort((a, b) => (a.sort || 0) - (b.sort || 0))
        .map(buildNode)
        .filter(Boolean) as OrganizationTreeNode[];

      // 如果本身不匹配且没有匹配的子节点，则过滤掉
      if (!matchSearch && childNodes.length === 0) {
        return null;
      }

      // 高亮搜索词
      let titleText: React.ReactNode = org.name;
      if (searchText && org.name.toLowerCase().includes(searchText.toLowerCase())) {
        const index = org.name.toLowerCase().indexOf(searchText.toLowerCase());
        const beforeStr = org.name.substring(0, index);
        const matchStr = org.name.substring(index, index + searchText.length);
        const afterStr = org.name.substring(index + searchText.length);
        titleText = (
          <>
            {beforeStr}
            <span className="search-highlight">{matchStr}</span>
            {afterStr}
          </>
        );
      }

      return {
        key: String(org.id),
        title: (
          <div className="org-tree-node-content">
            <span className="org-tree-node-icon">{typeConfig.icon}</span>
            <span className="org-tree-node-title">{titleText}</span>
            <Space size={4} className="org-tree-node-tags">
              <Tag color={typeConfig.color} className="org-tree-node-tag">
                {typeConfig.text}
              </Tag>
              <Tag color={levelConfig.color} className="org-tree-node-tag">
                {levelConfig.text}
              </Tag>
            </Space>
            {org.type === OrganizationType.COMPANY && (
              <CrownOutlined className="org-tree-node-crown" />
            )}
            {((org as any).leaderName || org.leader_name) && (
              <span className="org-tree-node-leader">
                <UserOutlined /> {(org as any).leaderName || org.leader_name}
              </span>
            )}
            <span className="org-tree-node-count">
              ({(org as any).memberCount ?? org.member_count}人)
            </span>
          </div>
        ),
        children: childNodes.length > 0 ? childNodes : undefined,
        organization: org,
        isLeaf: children.length === 0,
        selectable: true,
        disabled: false,
      };
    };

    // 获取根节点
    const rootOrgs = orgs.filter(org => {
      const parentId = (org as any).parentId ?? org.parent_id;
      return !parentId || String(parentId) === '0';
    });
    return rootOrgs
      .sort((a, b) => (a.sort || 0) - (b.sort || 0))
      .map(buildNode)
      .filter(Boolean) as OrganizationTreeNode[];
  }, []);

  // 树形数据
  const treeData = useMemo(() => {
    return buildTreeData(organizations, searchValue);
  }, [organizations, searchValue, buildTreeData]);

  // 获取所有父级keys（用于搜索时自动展开）
  const getParentKeys = useCallback((orgs: Organization[], searchText: string): string[] => {
    if (!searchText) return [];

    const parentKeys: string[] = [];
    const matchedOrgIds = new Set<string | number>();

    // 找到所有匹配的组织
    orgs.forEach(org => {
      if (
        org.name.toLowerCase().includes(searchText.toLowerCase()) ||
        org.code.toLowerCase().includes(searchText.toLowerCase())
      ) {
        matchedOrgIds.add(org.id);
      }
    });

    // 找到所有父级
    const findParents = (orgId: string | number | undefined) => {
      if (!orgId) return;
      const org = orgs.find(o => o.id === orgId);
      const parentId = org ? ((org as any).parentId ?? org.parent_id) : undefined;
      if (org && parentId) {
        parentKeys.push(String(parentId));
        findParents(parentId);
      }
    };

    matchedOrgIds.forEach(id => {
      const org = orgs.find(o => o.id === id);
      const parentId = org ? ((org as any).parentId ?? org.parent_id) : undefined;
      if (parentId) {
        parentKeys.push(String(parentId));
        findParents(parentId);
      }
    });

    return [...new Set(parentKeys)];
  }, []);

  // 搜索处理
  const handleSearch = useCallback((value: string) => {
    setSearchValue(value);
    if (value) {
      const parentKeys = getParentKeys(organizations, value);
      if (!propsExpandedKeys) {
        setInternalExpandedKeys(parentKeys);
      }
      setAutoExpandParent(true);
    }
  }, [organizations, getParentKeys, propsExpandedKeys]);

  // 展开/收起处理
  const handleExpand = useCallback((keys: React.Key[]) => {
    const stringKeys = keys.map(String);
    if (onExpand) {
      onExpand(stringKeys);
    } else {
      setInternalExpandedKeys(stringKeys);
    }
    setAutoExpandParent(false);
  }, [onExpand]);

  // 选中处理
  const handleSelect = useCallback((keys: React.Key[], info: { node: TreeDataNode }) => {
    if (keys.length > 0 && onSelect) {
      const node = info.node as unknown as OrganizationTreeNode;
      if (node.organization) {
        onSelect(node.organization);
      }
    }
  }, [onSelect]);

  // 拖拽处理
  const handleDrop = useCallback((info: { node: TreeDataNode; dragNode: TreeDataNode; dropPosition: number; dropToGap: boolean }) => {
    if (!onDrop) return;

    const dropNode = info.node as unknown as OrganizationTreeNode;
    const dragNode = info.dragNode as unknown as OrganizationTreeNode;
    const dropPosition = info.dropPosition;
    const dropToGap = info.dropToGap;

    // 检查是否试图将公司类型拖入其他节点下
    if (dragNode.organization.type === OrganizationType.COMPANY && !dropToGap) {
      message.warning('公司类型组织不能作为其他组织的子组织');
      return;
    }

    // 检查是否跨层级移动
    const dragParentId = (dragNode.organization as any).parentId ?? dragNode.organization.parent_id;
    const dropParentId = (dropNode.organization as any).parentId ?? dropNode.organization.parent_id;
    const isCrossLevel = dragParentId !== dropNode.organization.id &&
      dragParentId !== dropParentId;

    if (isCrossLevel && !dropToGap) {
      modal.confirm({
        title: '确认移动',
        content: `确定要将"${dragNode.organization.name}"移动到"${dropNode.organization.name}"下吗？`,
        onOk: () => {
          onDrop({
            node: dropNode,
            dragNode,
            dragNodesKeys: [String(dragNode.organization.id)],
            dropPosition,
            dropToGap,
          });
        },
      });
    } else {
      onDrop({
        node: dropNode,
        dragNode,
        dragNodesKeys: [String(dragNode.organization.id)],
        dropPosition,
        dropToGap,
      });
    }
  }, [onDrop]);

  // 右键菜单
  const getContextMenuItems = useCallback((org: Organization): ContextMenuItem[] => {
    const items: ContextMenuItem[] = [];

    if (onViewDetail) {
      items.push({
        key: 'view',
        label: '查看详情',
        icon: <EyeOutlined />,
        onClick: () => onViewDetail(org),
      });
    }

    if (onEdit) {
      items.push({
        key: 'edit',
        label: '编辑组织',
        icon: <EditOutlined />,
        onClick: () => onEdit(org),
      });
    }

    if (onAddChild) {
      items.push({
        key: 'addChild',
        label: '添加子组织',
        icon: <PlusOutlined />,
        onClick: () => onAddChild(org),
      });
    }

    if (onViewMembers) {
      items.push({
        key: 'members',
        label: '查看成员',
        icon: <TeamOutlined />,
        onClick: () => onViewMembers(org),
      });
    }

    if (onDelete) {
      items.push({
        key: 'delete',
        label: '删除组织',
        icon: <DeleteOutlined />,
        danger: true,
        disabled: org.type === OrganizationType.COMPANY,
        onClick: () => onDelete(org),
      });
    }

    return items;
  }, [onViewDetail, onEdit, onAddChild, onViewMembers, onDelete]);

  // 渲染树节点标题（带右键菜单）
  const renderTreeNode = useCallback((nodeData: TreeDataNode): React.ReactNode => {
    const node = nodeData as OrganizationTreeNode;
    if (!showActions) {
      return node.title;
    }

    return (
      <Dropdown
        menu={{
          items: getContextMenuItems(node.organization).map(item => ({
            key: item.key,
            label: item.label,
            icon: item.icon,
            danger: item.danger,
            disabled: item.disabled,
            onClick: item.onClick,
          })),
        }}
        trigger={['contextMenu']}
      >
        <span className="org-tree-node-wrapper">{node.title}</span>
      </Dropdown>
    );
  }, [showActions, getContextMenuItems]);

  if (loading) {
    return (
      <div className={`org-tree-loading ${className || ''}`} style={style}>
        <Spin tip="加载中..."><div style={{ minHeight: 100 }} /></Spin>
      </div>
    );
  }

  if (organizations.length === 0) {
    return (
      <div className={`org-tree-empty ${className || ''}`} style={style}>
        <Empty description="暂无组织数据" />
      </div>
    );
  }

  return (
    <div className={`org-tree-container ${className || ''}`} style={style}>
      {showSearch && (
        <div className="org-tree-search">
          <Search
            placeholder="搜索组织名称或编码"
            allowClear
            prefix={<SearchOutlined />}
            onSearch={handleSearch}
            onChange={e => handleSearch(e.target.value)}
            style={{ marginBottom: 16 }}
          />
        </div>
      )}
      <Tree
        className="org-tree"
        treeData={treeData}
        expandedKeys={expandedKeys}
        autoExpandParent={autoExpandParent}
        selectedKeys={selectedKey ? [selectedKey] : []}
        onExpand={handleExpand}
        onSelect={handleSelect}
        draggable={draggable ? { icon: false } : false}
        onDrop={handleDrop}
        showLine={{ showLeafIcon: false }}
        showIcon={false}
        switcherIcon={<DownOutlined />}
        titleRender={renderTreeNode}
        height={height}
        blockNode
      />
    </div>
  );
};

export default OrganizationTree;
export type { OrganizationTreeProps, OrganizationTreeNode, DragInfo };
